// Shared studio filter logic for TouchTour Studio (WP-E).
//
// The filter shape lifted into NodeEditor is:
//   { status: string[], floor: string|null, tags: string[] }
//
// This module is the single source of truth for "does this node pass the
// current filter?" so the canvas (TourCanvas) and the minimap (StudioInspector)
// always agree on what's visible. WP-0 wired the filter prop through and applied
// tags/floor; WP-E completes the status axis and the floor → image mapping.

import { getLinkStatus } from "./sceneStatus";

// The four status options surfaced as checkboxes in the FiltersPanel.
export const STATUS_OPTIONS = [
  { value: "placed", label: "Placed" },
  { value: "linked", label: "Linked" },
  { value: "unlinked", label: "Unlinked" },
  { value: "unplaced", label: "Unplaced" },
];

// Floors don't store their members on the image; a floorplan references the
// scenes that sit on it via its hotspots (hotspot.image === image id). Build a
// imageId → floor uuid index once so floor filtering is a cheap lookup.
//
// An image referenced by several floors is assigned to the first one found,
// which is good enough for the "show this floor only" filter.
export const buildFloorIndex = (floorplans = []) => {
  const index = {};
  (floorplans ?? []).forEach(floorplan => {
    (floorplan?.hotspots ?? []).forEach(hotspot => {
      const imageId = hotspot?.image;
      if (imageId != null && index[imageId] == null) {
        index[imageId] = floorplan.uuid;
      }
    });
  });
  return index;
};

// The floor a board node belongs to: an explicit image.floor wins (forward
// compatible with an optional persisted field), otherwise the derived index.
export const getImageFloor = (image, floorIndex = {}) => {
  if (image?.floor != null && image.floor !== "") return image.floor;
  return floorIndex?.[image?.id] ?? null;
};

// True when a board node should be visible under the given filter.
// `floorIndex` comes from buildFloorIndex(tour.data.floorplans).
//
// Status semantics: every node on the board is "placed" and is either "linked"
// or "unlinked"; "unplaced" only ever applies to scenes that aren't on the
// board (handled by the ScenesPanel). The checkboxes are OR'd together, so an
// empty selection means "show everything".
export const matchesStudioFilter = (image, filter, floorIndex = {}) => {
  if (!filter || !image) return true;

  const tags = filter.tags ?? [];
  const status = filter.status ?? [];
  const floor = filter.floor ?? null;

  if (tags.length > 0 && !tags.includes(image.tag ?? "Untagged")) {
    return false;
  }

  if (floor != null && floor !== "" && getImageFloor(image, floorIndex) !== floor) {
    return false;
  }

  if (status.length > 0) {
    const nodeStatuses = ["placed", getLinkStatus(image)];
    if (!status.some(value => nodeStatuses.includes(value))) {
      return false;
    }
  }

  return true;
};
